/**
 * Chart colour tokens and scale helpers.
 *
 * ── Why the charts are not navy ───────────────────────────────────────────────
 * The brand navy (#2c3e50) was run through the data-viz palette validator and
 * fails two hard checks as a data colour:
 *
 *     [FAIL] Lightness band    0.356, outside the 0.43–0.77 band
 *     [FAIL] Chroma floor      0.039, below the 0.1 floor (reads grey)
 *
 * At that lightness and chroma it is indistinguishable from a dark gridline, so
 * a navy line on a chart reads as chrome rather than data. The navy therefore
 * stays where it belongs — sidebar, headings, buttons — and the marks use the
 * blue family, which is close enough in hue to look of a piece with it.
 *
 * Everything below is validated output, not taste:
 *
 *   SERIES          #2a78d6 — passes all six checks on the #fcfcfb surface.
 *   CONTEXT         #898781 — the de-emphasis grey for a comparison series. It
 *                   deliberately fails the chroma floor, because that check is
 *                   for *categorical* palettes where every colour must read as a
 *                   distinct identity. This is the "emphasis" form instead: one
 *                   hue carries the subject, grey carries the context.
 *   PIPELINE        a 5-step single-hue ordinal ramp, validated with --ordinal:
 *                   monotone lightness, every adjacent gap ≥ 0.06, light end at
 *                   2.06:1 against the surface, hue spread 3°.
 *   STATUS          the fixed status palette. Never reused as a series colour,
 *                   and always shipped with an icon or a text label so that hue
 *                   is not the only carrier of meaning.
 */

// --- Data marks -------------------------------------------------------------
export const SERIES = '#2a78d6'
export const SERIES_SOFT = '#cde2fb' // area fill under the line
export const CONTEXT = '#898781'

/**
 * Order pipeline, light → dark in pipeline order. Ordinal rather than
 * categorical: the stages have a natural sequence, so a ramp communicates
 * "further along" in a way eight unrelated hues cannot.
 */
export const PIPELINE = {
  pending: '#86b6ef',
  paid: '#5598e7',
  processing: '#2a78d6',
  shipped: '#1c5cab',
  delivered: '#104281',
  // Not part of the ramp — an exception, so it takes the status colour.
  cancelled: '#d03b3b',
  refunded: '#ec835a',
}

// --- Status (fixed, never themed) ------------------------------------------
export const STATUS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
  goodText: '#006300',
}

// --- Chrome and ink --------------------------------------------------------
export const CHROME = {
  surface: '#fcfcfb',
  plane: '#f9f9f7',
  ink: '#0b0b0b',
  inkSecondary: '#52514e',
  inkMuted: '#898781',
  grid: '#e1e0d9',
  baseline: '#c3c2b7',
}

// ---------------------------------------------------------------------------
//  Scale helpers
// ---------------------------------------------------------------------------

/**
 * A "nice" upper bound for a value axis — 1, 2, 2.5 or 5 × a power of ten.
 *
 * Without this the top gridline lands on 8 437 and the reader has to do
 * arithmetic to compare two charts. Snapping to a round number is what makes an
 * axis skimmable.
 */
export function niceMax(value) {
  const n = Number(value) || 0
  if (n <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(n))
  const normalised = n / magnitude
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 2.5 ? 2.5 : normalised <= 5 ? 5 : 10
  return step * magnitude
}

/** Evenly spaced tick values from 0 to max inclusive. */
export function ticks(max, count = 4) {
  return Array.from({ length: count + 1 }, (_, i) => (max / count) * i)
}

/**
 * Build an SVG path for a series.
 *
 * Straight segments, not a spline. A smoothed curve invents values between the
 * points it was given — on daily revenue that means drawing sales on a Sunday
 * when the shop is shut.
 */
export function linePath(points) {
  if (points.length === 0) return ''
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')
}

/** The same path closed down to the baseline, for an area fill. */
export function areaPath(points, baselineY) {
  if (points.length === 0) return ''
  const first = points[0]
  const last = points[points.length - 1]
  return `${linePath(points)} L${last.x.toFixed(2)},${baselineY.toFixed(2)} L${first.x.toFixed(2)},${baselineY.toFixed(2)} Z`
}
